<template>
    <v-app>
        <lc-bank-setup :dialog="dialog" :data="editData" @update:dialog="dialog = $event" />
        <v-container fluid>
            <v-row>
                <v-col cols="12">
                    <v-card>
                        <v-card-title class="bg-blue-grey-lighten-4 d-flex align-center justify-space-between">
                            <span class="text-h6">LC Bank Details</span>
                            <v-btn
                                color="primary"
                                variant="text"
                                icon="mdi-arrow-left"
                                @click="goBack"
                                title="Back to List"
                            ></v-btn>
                        </v-card-title>

                        <v-card-text v-if="loading" class="text-center py-10">
                            <v-progress-circular
                                indeterminate
                                color="primary"
                                size="50"
                            ></v-progress-circular>
                            <p class="mt-4">Loading bank details...</p>
                        </v-card-text>

                        <v-card-text v-else-if="bankDetail" class="pa-6">
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-card variant="outlined" class="mb-4">
                                        <v-card-title class="bg-grey-lighten-4 text-subtitle-1">
                                            Basic Information
                                        </v-card-title>
                                        <v-card-text>
                                            <v-list density="compact">
                                                <v-list-item>
                                                    <template v-slot:prepend>
                                                        <v-icon color="primary">mdi-barcode</v-icon>
                                                    </template>
                                                    <v-list-item-title class="font-weight-medium">Code</v-list-item-title>
                                                    <v-list-item-subtitle>{{ bankDetail.code || 'N/A' }}</v-list-item-subtitle>
                                                </v-list-item>

                                                <v-divider class="my-2"></v-divider>

                                                <v-list-item>
                                                    <template v-slot:prepend>
                                                        <v-icon color="primary">mdi-bank</v-icon>
                                                    </template>
                                                    <v-list-item-title class="font-weight-medium">Bank Name</v-list-item-title>
                                                    <v-list-item-subtitle>{{ bankDetail.name || 'N/A' }}</v-list-item-subtitle>
                                                </v-list-item>

                                                <v-divider class="my-2"></v-divider>

                                                <v-list-item>
                                                    <template v-slot:prepend>
                                                        <v-icon color="primary">mdi-source-branch</v-icon>
                                                    </template>
                                                    <v-list-item-title class="font-weight-medium">Branch</v-list-item-title>
                                                    <v-list-item-subtitle>{{ bankDetail.branch || 'N/A' }}</v-list-item-subtitle>
                                                </v-list-item>

                                                <v-divider class="my-2"></v-divider>

                                                <v-list-item>
                                                    <template v-slot:prepend>
                                                        <v-icon color="primary">mdi-swap-horizontal</v-icon>
                                                    </template>
                                                    <v-list-item-title class="font-weight-medium">SWIFT Code</v-list-item-title>
                                                    <v-list-item-subtitle>{{ bankDetail.swift_code || 'N/A' }}</v-list-item-subtitle>
                                                </v-list-item>

                                                <v-divider class="my-2"></v-divider>

                                                <v-list-item>
                                                    <template v-slot:prepend>
                                                        <v-icon color="primary">mdi-check-circle</v-icon>
                                                    </template>
                                                    <v-list-item-title class="font-weight-medium">Status</v-list-item-title>
                                                    <v-list-item-subtitle>
                                                        <v-chip 
                                                            :color="bankDetail.status == 1 ? 'success' : 'error'" 
                                                            size="small"
                                                            class="mt-1"
                                                        >
                                                            {{ bankDetail.status == 1 ? 'Active' : 'Inactive' }}
                                                        </v-chip>
                                                    </v-list-item-subtitle>
                                                </v-list-item>
                                            </v-list>
                                        </v-card-text>
                                    </v-card>
                                </v-col>

                                <v-col cols="12" md="6">
                                    <v-card variant="outlined" class="mb-4">
                                        <v-card-title class="bg-grey-lighten-4 text-subtitle-1">
                                            Additional Information
                                        </v-card-title>
                                        <v-card-text>
                                            <v-list density="compact">
                                                <v-list-item>
                                                    <template v-slot:prepend>
                                                        <v-icon color="primary">mdi-map-marker</v-icon>
                                                    </template>
                                                    <v-list-item-title class="font-weight-medium">Address</v-list-item-title>
                                                    <v-list-item-subtitle>{{ bankDetail.address || 'N/A' }}</v-list-item-subtitle>
                                                </v-list-item>

                                                <v-divider class="my-2"></v-divider>

                                                <v-list-item>
                                                    <template v-slot:prepend>
                                                        <v-icon color="primary">mdi-note-text</v-icon>
                                                    </template>
                                                    <v-list-item-title class="font-weight-medium">Remarks</v-list-item-title>
                                                    <v-list-item-subtitle>{{ bankDetail.remarks || 'N/A' }}</v-list-item-subtitle>
                                                </v-list-item>

                                                <v-divider class="my-2"></v-divider>

                                                <v-list-item v-if="bankDetail.created_by">
                                                    <template v-slot:prepend>
                                                        <v-icon color="primary">mdi-account-plus</v-icon>
                                                    </template>
                                                    <v-list-item-title class="font-weight-medium">Created By</v-list-item-title>
                                                    <v-list-item-subtitle>{{ bankDetail.created_by?.full_name || 'N/A' }}</v-list-item-subtitle>
                                                </v-list-item>

                                                <v-divider class="my-2"></v-divider>

                                                <v-list-item v-if="bankDetail.updated_by">
                                                    <template v-slot:prepend>
                                                        <v-icon color="primary">mdi-account-edit</v-icon>
                                                    </template>
                                                    <v-list-item-title class="font-weight-medium">Updated By</v-list-item-title>
                                                    <v-list-item-subtitle>{{ bankDetail.updated_by?.full_name || 'N/A' }}</v-list-item-subtitle>
                                                </v-list-item>

                                                <v-divider class="my-2"></v-divider>

                                                <v-list-item v-if="bankDetail.created_at">
                                                    <template v-slot:prepend>
                                                        <v-icon color="primary">mdi-calendar-clock</v-icon>
                                                    </template>
                                                    <v-list-item-title class="font-weight-medium">Created At</v-list-item-title>
                                                    <v-list-item-subtitle>{{ formatDate(bankDetail.created_at) }}</v-list-item-subtitle>
                                                </v-list-item>

                                                <v-divider class="my-2"></v-divider>

                                                <v-list-item v-if="bankDetail.updated_at">
                                                    <template v-slot:prepend>
                                                        <v-icon color="primary">mdi-update</v-icon>
                                                    </template>
                                                    <v-list-item-title class="font-weight-medium">Updated At</v-list-item-title>
                                                    <v-list-item-subtitle>{{ formatDate(bankDetail.updated_at) }}</v-list-item-subtitle>
                                                </v-list-item>
                                            </v-list>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <v-row>
                                <v-col cols="12" class="text-right">
                                    <v-btn
                                        color="secondary"
                                        variant="outlined"
                                        prepend-icon="mdi-arrow-left"
                                        @click="goBack"
                                        class="mr-2"
                                    >
                                        Back to List
                                    </v-btn>
                                    <v-btn
                                        v-if="$updateAccess($route.fullPath)"
                                        color="primary"
                                        variant="tonal"
                                        prepend-icon="mdi-pencil"
                                        @click="editBank"
                                    >
                                        Edit
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-card-text>

                        <v-card-text v-else class="text-center py-10">
                            <v-icon color="error" size="64">mdi-alert-circle</v-icon>
                            <p class="mt-4 text-h6">Bank not found</p>
                            <v-btn color="primary" @click="goBack" class="mt-4">
                                Go Back
                            </v-btn>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
    </v-app>
</template>

<script>
import lcBankSetup from '@/components/module/lc/settingsManagement/lcBankSetup.vue';

export default {
    components: {
        lcBankSetup
    },
    data: () => ({
        loading: false,
        bankId: null,
        dialog: false,
        editData: {},
    }),
    created() {
        this.bankId = this.$route.params.id;
        this.fetchBankDetail();
    },
    computed: {
        bankDetail() {
            return this.$store.getters.getLcBankDetail;
        },
    },
    methods: {
        fetchBankDetail() {
            this.loading = true;
            this.$store.dispatch('getLcBankById', this.bankId)
                .then(() => {
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    this.$showSnackbar({
                        message: error.response?.data?.message || 'Failed to load bank details',
                        color: 'error'
                    });
                });
        },
        goBack() {
            this.$router.push({ name: 'lc-banks' });
        },
        editBank() {
            this.editData = { ...this.bankDetail };
            this.dialog = true;
        },
        formatDate(date) {
            if (!date) return 'N/A';
            const d = new Date(date);
            return d.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
    },
    watch: {
        '$route.params.id'(newId) {
            if (newId) {
                this.bankId = newId;
                this.fetchBankDetail();
            }
        },
        dialog(newVal, oldVal) {
            // Refresh data when dialog closes after edit
            if (oldVal === true && newVal === false) {
                this.fetchBankDetail();
            }
        }
    }
}
</script>

<style scoped>
.v-list-item {
    min-height: 48px;
}

.v-list-item-title {
    font-size: 0.875rem;
}

.v-list-item-subtitle {
    font-size: 0.875rem;
    opacity: 0.8;
}
</style>

